import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import PasswordStrengthBar from "react-password-strength-bar";
import { registerForm, updateUserInfo } from "../services/authForm";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import UserSidebar from "../components/UserSidebar";
import Header from "../../dashboard/components/Header";
import Footer from "units/dashboard/components/Footer";
import LOGORayaBOT from "assets/images/logo8.png";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../dashboard/store/userSlice";
import { useTranslation } from "react-i18next";

// 🔹 کلاس‌های مشترک برای تم یکپارچه
const inputBaseClass =
  "mt-2.5 block w-full rounded-xl px-3.5 py-2.5 text-base bg-white/90 dark:!bg-zinc-900/80 backdrop-blur-md shadow-sm text-gray-900 dark:!text-white placeholder:text-gray-500 dark:placeholder:!text-zinc-400 outline outline-1 -outline-offset-1 transition-all duration-300 ease-out focus:outline focus:outline-2 focus:-outline-offset-2 focus:ring-4 focus:ring-[#FFC505]/20 dark:focus:!ring-[#FFC505]/20 focus:shadow-[0_0_0_4px_rgba(255,197,5,0.08)]";
const labelClass =
  "block text-sm font-semibold text-gray-800 dark:!text-gray-200 transition-colors";
const errorClass =
  "text-red-500 dark:!text-red-400 text-xs mt-1.5 animate-[fadeIn_.2s_ease-out]";
const hintClass = "text-gray-500 dark:!text-gray-400 text-xs mt-1.5";

const StarField = () => {
  const [stars, setStars] = useState([]);

  useEffect(() => {
    const generatedStars = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 5,
      duration: Math.random() * 3 + 2,
      opacity: Math.random() * 0.7 + 0.3,
    }));
    setStars(generatedStars);
  }, []);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      {/* گرادیانت پس‌زمینه */}
      <div
        className="absolute inset-0 
                           bg-gradient-to-br 
                           from-slate-50 
                           via-white 
                           to-teal-50/60
                           dark:!from-teal-950 
                           dark:!via-gray-950 
                           dark:!to-black"
      />

      {/* لایه اضافی */}
      <div
        className="absolute inset-0 opacity-40
                           bg-gradient-to-tr 
                           from-transparent 
                           via-amber-50/40 
                           to-transparent
                           dark:!from-transparent 
                           dark:!via-teal-900/20 
                           dark:!to-black/40"
      />

      {/* دایره‌های رنگی */}
      <div className="absolute -top-32 -right-32 w-96 h-96 
                            bg-gradient-to-br from-teal-300/40 to-teal-500/20 
                            dark:!from-teal-600/30 dark:!to-teal-800/20
                            rounded-full blur-3xl animate-float" />

      <div
        className="absolute -bottom-32 -left-32 w-96 h-96 
                           bg-gradient-to-br from-amber-300/40 to-amber-500/20 
                           dark:!from-amber-600/20 dark:!to-amber-800/10
                           rounded-full blur-3xl animate-float"
        style={{ animationDelay: "500ms" }}
      />

      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                           w-[600px] h-[600px] 
                           bg-gradient-to-br from-teal-200/30 via-amber-100/30 to-teal-200/30 
                           dark:!from-teal-800/20 dark:!via-teal-900/15 dark:!to-teal-950/20
                           rounded-full blur-3xl animate-float"
        style={{ animationDelay: "300ms" }}
      />

      {/* ستاره‌ها */}
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full 
                               bg-gradient-to-br from-amber-400 to-teal-400 
                               dark:!from-amber-300 dark:!to-teal-300"
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
            boxShadow: `0 0 ${star.size * 3}px rgba(212, 175, 55, 0.6)`,
          }}
        />
      ))}

      <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.3); }
                }
                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }
            `}</style>
    </div>
  );
};

export default function AuthForm() {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  const [usernameLive, setUsernameLive] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (location.pathname === "/user/edit-info") {
      setIsEditMode(true);
    }
  }, [location]);

  const userInfo = useSelector((state) => state.userInfo);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required(t("authForm.validation.firstNameRequired"))
      .matches(/^[\u0600-\u06FF0-9\s]+$/, t("authForm.validation.persianOnly")),
    lastName: Yup.string()
      .required(t("authForm.validation.lastNameRequired"))
      .matches(/^[\u0600-\u06FF0-9\s]+$/, t("authForm.validation.persianOnly")),
    name: Yup.string()
      .required(t("authForm.validation.assistantNameRequired"))
      .matches(/^[\u0600-\u06FF0-9\s]+$/, t("authForm.validation.persianOnly")),
    username: Yup.string()
      .required(t("authForm.validation.usernameRequired"))
      .matches(/^[a-z0-9]+$/, t("authForm.validation.usernameFormat")),
    password: isEditMode
      ? Yup.string().notRequired()
      : Yup.string()
        .required(t("authForm.validation.passwordRequired"))
        .min(6, t("authForm.validation.passwordMinLength")),
    confirmPassword: isEditMode
      ? Yup.string().notRequired()
      : Yup.string()
        .required(t("authForm.validation.confirmPasswordRequired"))
        .oneOf(
          [Yup.ref("password"), null],
          t("authForm.validation.passwordMismatch"),
        ),
    email: Yup.string()
      .required(t("authForm.validation.emailRequired"))
      .test(
        "is-valid-email",
        t("authForm.validation.emailFormat"),
        (value) =>
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value),
      ),
    acceptTerms: isEditMode
      ? Yup.mixed().notRequired()
      : Yup.bool().oneOf([true], t("authForm.validation.termsRequired")),
    orgName: Yup.string()
      .required(t("authForm.validation.orgNameRequired"))
      .matches(/^[\u0600-\u06FF0-9\s]+$/, t("authForm.validation.persianOnly")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (formData) => {
    try {
      delete formData.acceptTerms;
      if (isEditMode) {
        delete formData.password;
        delete formData.confirmPassword;
        delete formData.username;
      }
      if (isEditMode) {
        const res = await updateUserInfo({ formData, accessToken });
        if (res?.ok) {
          const updatedUser = {
            ...userInfo,
            firstName: formData.firstName,
            lastName: formData.lastName,
            orgName: formData.orgName,
            name: formData.name,
            email: formData.email,
          };
          dispatch(setUser(updatedUser));
          toast.success(t("authForm.toasts.updateSuccess"));
        }
      } else {
        const res = await registerForm(formData, accessToken);
        if (res?.ok) {
          toast.success(t("authForm.toasts.registerSuccess"));
          navigate("/user/dashboard");
        }
      }
    } catch (error) {
      console.error("خطا در ارسال فرم:", error);
    }
  };

  const usernameWatch = watch("username", "");

  const convertToEnglishNumbers = (str) => {
    const persianNumbers = "۰۱۲۳۴۵۶۷۸۹";
    const arabicNumbers = "٠١٢٣٤٥٦٧٨٩";
    return str
      .split("")
      .map((ch) => {
        if (persianNumbers.includes(ch)) return persianNumbers.indexOf(ch);
        if (arabicNumbers.includes(ch)) return arabicNumbers.indexOf(ch);
        return ch;
      })
      .join("");
  };

  useEffect(() => {
    if (isEditMode && userInfo) {
      setValue("firstName", userInfo.firstName || "");
      setValue("lastName", userInfo.lastName || "");
      setValue("email", userInfo.email || "");
      setValue("username", userInfo.username || "");
      setValue("name", userInfo.name || "");
      setValue("orgName", userInfo.orgName || "");
      setUsernameLive(userInfo.username || "");
    }
  }, [isEditMode, userInfo, setValue]);

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px);} to {opacity:1; transform:none;} }
        @keyframes floatBlob { 0%,100% { transform: translate3d(0,0,0) rotate(30deg);} 50% { transform: translate3d(-20px,-30px,0) rotate(35deg);} }
        @keyframes shine { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes pulseGlow { 0%,100% { box-shadow: 0 8px 30px -8px rgba(28,71,71,.45), 0 0 0 0 rgba(255,197,5,.35);} 50% { box-shadow: 0 12px 40px -8px rgba(28,71,71,.6), 0 0 0 8px rgba(255,197,5,0);} }
        @keyframes floatUp { from { opacity: 0; transform: translateY(12px);} to { opacity: 1; transform: none;} }
        .blob-anim { animation: floatBlob 14s ease-in-out infinite; }
        .btn-glow { animation: pulseGlow 3s ease-in-out infinite; }
        .field-fade { animation: floatUp .5s ease-out backwards; }
        .field-fade:nth-child(1) { animation-delay: .05s; }
        .field-fade:nth-child(2) { animation-delay: .1s; }
        .field-fade:nth-child(3) { animation-delay: .15s; }
        .field-fade:nth-child(4) { animation-delay: .2s; }
        .field-fade:nth-child(5) { animation-delay: .25s; }
        .field-fade:nth-child(6) { animation-delay: .3s; }
        .field-fade:nth-child(7) { animation-delay: .35s; }
        .field-fade:nth-child(8) { animation-delay: .4s; }
      `}</style>

      {isEditMode && (
        <>
          <nav className="border-b border-slate-100 dark:!bg-zinc-800/90 dark:!border-zinc-700 backdrop-blur-md print:hidden flex items-center fixed top-0 right-0 left-0 bg-white/80 z-10">
            <Header />
          </nav>

          <div className="vertical-menu rtl:right-0 fixed ltr:left-0 bottom-0 top-16 h-screen border-r bg-slate-50/80 backdrop-blur-md border-gray-50 print:hidden dark:!bg-zinc-800/90 dark:!border-neutral-700 z-10">
            <UserSidebar />
          </div>
        </>
      )}

      <div className="flex flex-col min-h-screen ">
        <StarField />
        <div
          className={`min-h-screen ${isEditMode ? "main-content flex-grow " : " flex-grow mx-auto md:mx-0"}`}
        >
          <div
            className={` ${!isEditMode ? "flex flex-row-reverse items-stretch min-h-screen w-full mx-auto" : "page-content mt-1"}`}
          >
            {isEditMode && (
              <div className="container-fluid px-[0.625rem]">
                <div className="grid grid-cols-1 mb-2">
                  <div className="flex items-center justify-between">
                    {isEditMode && (
                      <h4 className="mb-sm-0 text-lg sm:text-2xl font-semibold grow text-transparent bg-clip-text bg-gradient-to-l from-[#1c4747] to-[#296c6c] dark:!from-[#FFC505] dark:!to-[#ffe066]">
                        {t("authForm.editTitle")}
                      </h4>
                    )}
                    <nav className="flex" aria-label="Breadcrumb"></nav>
                  </div>
                </div>

                <h1 className="text-md sm:text-lg mr-2 text-gray-600 dark:!text-gray-300">
                  {t("authForm.editSubtitle")}{" "}
                </h1>
              </div>
            )}

            <div className="isolate bg-transparent px-4 sm:px-6 py-5 sm:py-5 lg:px-8 relative flex-1">
              <ToastContainer
                containerId="main-toast"
                position="top-right"
                autoClose={3000}
                toastClassName="!rounded-xl !backdrop-blur-md dark:!bg-zinc-800/90"
              />

              {/* Blobs تزئینی */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
              >
                <div
                  style={{
                    clipPath:
                      "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
                  }}
                  className="blob-anim relative left-1/2 -z-10 aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#FFC505] via-[#7fb8b8] to-[#1c4747] opacity-40 dark:!opacity-25 sm:left-[calc(50%-40rem)] sm:w-[72.1875rem]"
                />
              </div>

              {!isEditMode && (
                <div className="mx-auto max-w-2xl text-center mt-8 sm:mt-10">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-3 rounded-full bg-white/60 dark:!bg-zinc-800/60 backdrop-blur-md border border-[#FFC505]/30 dark:!border-[#FFC505]/20 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-[#FFC505] animate-pulse" />
                    <span className="text-xs font-medium text-[#1c4747] dark:!text-[#FFC505]">
                      {t("authForm.registerBadge")}
                    </span>
                  </div>
                  <h2
                    className={`text-balance text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-l from-[#1c4747] via-[#296c6c] to-[#1c4747] dark:!from-[#FFC505] dark:!via-[#ffe066] dark:!to-[#FFC505] ${!isEditMode && "mt-2"}`}
                  >
                    {t("authForm.registerTitle")}
                  </h2>
                  <p className="mt-3 text-sm sm:text-base text-gray-600 dark:!text-gray-400">
                    {t("authForm.registerSubtitle")}
                  </p>

                  {/* خط جداکننده تزئینی طلایی */}
                  <div className="mt-6 flex items-center justify-center gap-2">
                    <span className="h-px w-16 bg-gradient-to-l from-transparent to-[#FFC505]/60" />
                    <span className="w-2 h-2 rotate-45 bg-[#FFC505] shadow-[0_0_12px_rgba(255,197,5,0.8)]" />
                    <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#FFC505]/60" />
                  </div>
                </div>
              )}

              {isEditMode && (
                <div className="mx-auto max-w-2xl text-center">
                  <h2
                    className={`text-balance text-lg sm:text-2xl font-semibold tracking-tight text-gray-900 dark:!text-gray-100 mt-4`}
                  >
                    {t("authForm.editFormTitle")}
                  </h2>
                </div>
              )}

              {/* فرم بدون کارت */}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className={`mx-auto mt-8 sm:mt-12 max-w-2xl space-y-6 ${!isEditMode ? "px-1 md:px-4 pb-10" : ""
                  }`}
              >
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                  {/* نام */}
                  <div className="field-fade">
                    <label className={labelClass}>
                      {t("authForm.fields.firstName")} *
                    </label>
                    <input
                      {...register("firstName")}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^\u0600-\u06FF\s‌]/g,
                          "",
                        );
                        setValue("firstName", value, { shouldValidate: true });
                      }}
                      className={`${inputBaseClass} ${errors.firstName
                        ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                        : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505]"
                        }`}
                    />
                    {errors.firstName && (
                      <p className={errorClass}>{errors.firstName.message}</p>
                    )}
                  </div>

                  {/* نام خانوادگی */}
                  <div className="field-fade">
                    <label className={labelClass}>
                      {t("authForm.fields.lastName")} *
                    </label>
                    <input
                      {...register("lastName")}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^\u0600-\u06FF\s‌]/g,
                          "",
                        );
                        setValue("lastName", value, { shouldValidate: true });
                      }}
                      className={`${inputBaseClass} ${errors.lastName
                        ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                        : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505]"
                        }`}
                    />
                    {errors.lastName && (
                      <p className={errorClass}>{errors.lastName.message}</p>
                    )}
                  </div>

                  {/* نام دستیار */}
                  <div className="field-fade">
                    <label className={labelClass}>
                      {t("authForm.fields.assistantName")} *
                    </label>
                    <input
                      {...register("name")}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^\u0600-\u06FF0-9\s]/g,
                          "",
                        );
                        setValue("name", value, { shouldValidate: true });
                      }}
                      className={`${inputBaseClass} ${errors.name
                        ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                        : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505]"
                        }`}
                    />
                    <p className={hintClass}>
                      {t("authForm.fields.assistantNameHint")}
                    </p>
                    {errors.name && (
                      <p className={errorClass}>{errors.name.message}</p>
                    )}
                  </div>

                  {/* نام کاربری */}
                  <div className="field-fade">
                    <label className={labelClass}>
                      {t("authForm.fields.username")} *
                    </label>
                    <input
                      {...register("username")}
                      onChange={(e) => {
                        const filtered = e.target.value.replace(
                          /[^a-z0-9]/g,
                          "",
                        );
                        setUsernameLive(filtered);
                        setValue("username", filtered, {
                          shouldValidate: true,
                        });
                      }}
                      value={usernameLive}
                      readOnly={isEditMode}
                      dir="ltr"
                      className={`${inputBaseClass} ${errors.username
                        ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                        : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505]"
                        } ${isEditMode
                          ? "bg-gray-100/60 dark:!bg-zinc-800/60 text-gray-500 dark:!text-gray-400 cursor-not-allowed pointer-events-none"
                          : ""
                        }`}
                    />
                    <p className={hintClass}>
                      {t("authForm.fields.usernameHint")}
                    </p>
                    <p className="text-[#1c4747] dark:!text-[#FFC505] text-sm mt-1 text-left font-mono bg-white/50 dark:!bg-zinc-800/50 rounded-lg px-2.5 py-1 inline-block border border-[#FFC505]/20">
                      rayabot.ir/
                      <span className="text-[#FFC505] dark:!text-[#ffe066] font-semibold">
                        {usernameLive || "..."}
                      </span>
                    </p>

                    {errors.username && (
                      <p className={errorClass}>{errors.username.message}</p>
                    )}
                  </div>

                  {!isEditMode && (
                    <>
                      <div className="relative field-fade">
                        <label className={labelClass}>
                          {t("authForm.fields.password")} *
                        </label>

                        <div className="relative mt-2.5">
                          <input
                            {...register("password")}
                            type={showPassword ? "text" : "password"}
                            placeholder={t("authForm.fields.passwordPlaceholder")}
                            className={`block w-full rounded-xl px-3.5 py-2.5 pr-11 text-base bg-white/60 dark:!bg-zinc-900/50 text-gray-900 dark:!text-gray-100 placeholder:text-gray-400 dark:!placeholder:text-gray-500 outline outline-1 -outline-offset-1 transition-all duration-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:ring-4 ${errors.password
                              ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                              : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505] focus:ring-[#FFC505]/20"
                              }`}
                          />

                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 left-0 flex items-center px-3 text-gray-500 dark:!text-gray-400 hover:text-[#1c4747] dark:hover:!text-[#FFC505] transition-colors duration-200 rounded-l-xl"
                          >
                            {showPassword ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3.98 8.223A10.477 10.477 0 0112 5.25c3.978 0 7.432 2.184 9.492 5.466a10.477 10.477 0 01-1.636 2.04M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3 3l18 18"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.458 12C3.732 7.943 7.522 5 12 5c4.478 0 8.268 2.943 9.542 7-.236.772-.554 1.508-.94 2.197M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                            )}
                          </button>
                        </div>

                        <p className={hintClass}>
                          {t("authForm.fields.passwordHint")}
                        </p>

                        <div className="mt-3 rounded-xl overflow-hidden">
                          <PasswordStrengthBar
                            password={watch("password")}
                            shortScoreWord={t("authForm.passwordStrength.veryWeak")}
                            scoreWords={[
                              t("authForm.passwordStrength.veryWeak"),
                              t("authForm.passwordStrength.weak"),
                              t("authForm.passwordStrength.medium"),
                              t("authForm.passwordStrength.good"),
                              t("authForm.passwordStrength.strong"),
                            ]}
                            barColors={[
                              "#ff6b6b",
                              "#ffb84d",
                              "#ffe66d",
                              "#9dd53a",
                              "#1c4747",
                            ]}
                            scoreWordStyle={{
                              color: "#1c4747",
                              fontFamily: "Vazirmatn, sans-serif",
                              fontSize: "0.875rem",
                            }}
                            style={{
                              direction: "ltr",
                              borderRadius: "8px",
                            }}
                          />
                        </div>

                        {errors.password && (
                          <p className={errorClass}>
                            {errors.password.message}
                          </p>
                        )}
                      </div>

                      {/* تکرار رمز عبور */}
                      <div className="relative field-fade">
                        <label className={labelClass}>
                          {t("authForm.fields.confirmPassword")} *
                        </label>

                        <div className="relative mt-2.5">
                          <input
                            {...register("confirmPassword")}
                            type={showRepeatPassword ? "text" : "password"}
                            placeholder={t("authForm.fields.confirmPasswordPlaceholder")}
                            className={`block w-full rounded-xl px-3.5 py-2.5 pr-11 text-base bg-white/60 dark:!bg-zinc-900/50 text-gray-900 dark:!text-gray-100 placeholder:text-gray-400 dark:!placeholder:text-gray-500 outline outline-1 -outline-offset-1 transition-all duration-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:ring-4 ${errors.confirmPassword
                              ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                              : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505] focus:ring-[#FFC505]/20"
                              }`}
                          />

                          <button
                            type="button"
                            onClick={() =>
                              setShowRepeatPassword(!showRepeatPassword)
                            }
                            className="absolute inset-y-0 left-0 flex items-center px-3 text-gray-500 dark:!text-gray-400 hover:text-[#1c4747] dark:hover:!text-[#FFC505] transition-colors duration-200 rounded-l-xl"
                          >
                            {showRepeatPassword ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3.98 8.223A10.477 10.477 0 0112 5.25c3.978 0 7.432 2.184 9.492 5.466a10.477 10.477 0 01-1.636 2.04M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3 3l18 18"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.458 12C3.732 7.943 7.522 5 12 5c4.478 0 8.268 2.943 9.542 7-.236.772-.554 1.508-.94 2.197M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                            )}
                          </button>
                        </div>

                        {errors.confirmPassword && (
                          <p className={errorClass}>
                            {errors.confirmPassword.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {/* ایمیل */}
                  <div className="field-fade">
                    <label className={labelClass}>
                      {t("authForm.fields.email")} *
                    </label>
                    <input
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /^[A-Za-z0-9]+$/,
                          "",
                        );
                        setValue("email", value, { shouldValidate: true });
                      }}
                      {...register("email")}
                      type="email"
                      dir="ltr"
                      className={`${inputBaseClass} ${errors.email
                        ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                        : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505]"
                        }`}
                    />
                    {errors.email && (
                      <p className={errorClass}>{errors.email.message}</p>
                    )}
                  </div>

                  {/* نام سازمان */}
                  <div className="field-fade">
                    <label className={labelClass}>
                      {t("authForm.fields.orgName")} *
                    </label>
                    <input
                      {...register("orgName")}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^\u0600-\u06FF\s‌]/g,
                          "",
                        );
                        setValue("orgName", value, { shouldValidate: true });
                      }}
                      className={`${inputBaseClass} ${errors.orgName
                        ? "outline-red-500 focus:outline-red-500 focus:ring-red-500/20"
                        : "outline-teal-700/35 dark:!outline-teal-300/35 focus:outline-[#FFC505]"
                        }`}
                    />
                    {errors.orgName && (
                      <p className={errorClass}>{errors.orgName.message}</p>
                    )}
                  </div>
                </div>

                {/* ✅ پذیرش شرایط و قوانین */}
                {!isEditMode && (
                  <div
                    className={`flex items-center gap-3 rounded-2xl p-3.5 border transition-all duration-300 ${errors.acceptTerms
                      ? "bg-red-50/70 dark:!bg-red-950/20 border-red-200 dark:!border-red-900/50"
                      : "bg-white/40 dark:!bg-zinc-800/40 border-[#FFC505]/20 dark:!border-[#FFC505]/10 hover:border-[#FFC505]/40"
                      } backdrop-blur-sm`}
                  >
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      disabled={isEditMode}
                      {...register("acceptTerms")}
                      className={`h-5 w-5 cursor-pointer rounded-md transition-all duration-200 ${errors.acceptTerms
                        ? "accent-red-500"
                        : "accent-[#1c4747] dark:!accent-[#FFC505]"
                        }`}
                    />

                    <label
                      htmlFor="acceptTerms"
                      className={`text-sm sm:text-md cursor-pointer select-none transition-colors ${errors.acceptTerms
                        ? "text-red-600 dark:!text-red-400"
                        : "text-gray-700 dark:!text-gray-300"
                        }`}
                    >
                      <a
                        href="/terms-and-conditions"
                        target="_blank"
                        className={`font-semibold underline underline-offset-4 transition-colors ${errors.acceptTerms
                          ? "text-red-600 dark:!text-red-400"
                          : "text-[#1c4747] dark:!text-[#FFC505] hover:text-[#296a6a] dark:hover:!text-[#ffe066]"
                          }`}
                      >
                        {t("authForm.terms.link")}{" "}
                      </a>
                      {t("authForm.terms.acceptSuffix")}
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-glow group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-l from-teal-500 via-teal-600 to-teal-700 px-4 py-3.5 text-center text-sm sm:text-base font-bold text-white  shadow-lg shadow-[#216363]  transition-all duration-300 hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFC505] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isEditMode
                      ? t("authForm.submit.edit")
                      : t("authForm.submit.register")}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                      />
                    </svg>
                  </span>
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                </button>
              </form>
            </div>
          </div>
          {isEditMode && <Footer />}
        </div>
      </div>
    </>
  );
}